/**
 * Экран «нет членства ни в одной фирме» — `FR-WEB-102`.
 *
 * Пустой перечень членств это законное состояние, а не отказ: человек вошёл, и
 * ему нужны объяснение и путь к созданию фирмы, а не пустой экран и не
 * сообщение об ошибке. Поэтому экран стоит в состоянии «пусто» и показывает
 * форму создания.
 *
 * Форма отправляет **ровно четыре поля контракта** и ничего сверх. Проверок в
 * клиенте нет ни одной: годность значений решает сервер, и отказ показывается
 * его же сообщениями — рядом с теми полями, которые он назвал. Ни одного имени
 * поля код при этом не читает: сопоставление идёт значением, пришедшим в
 * отказе, с атрибутом `name` элемента разметки.
 *
 * Тип фирмы — закрытый перечень **запроса**, а не правило: он объявлен формой
 * контракта, и перечень здесь типизирован ею, поэтому исчезновение значения из
 * контракта ломает типы. Тот же приём уже держит перечень языков.
 *
 * Смена языка не стирает введённое (долг `TD-72`, блок `WEB-2.1`): оболочка не
 * пересобирает экран, а просит его переподписаться — поля формы и всё, что в
 * них введено, остаются теми же узлами, меняются только подписи. Прежде экран
 * собирался заново из пустого шаблона, и три поля обнулялись.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { CompanyCreateRequest } from '../api/contract';
import { COMPANY_TYPES } from '../api/generated/enums';
import { companyTypeStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { navigate } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

type CompanyType = CompanyCreateRequest['type'];

/**
 * Первый вариант перечня — он же выбранный, пока человек не выбрал сам.
 *
 * Написанное руками значение здесь было бы вторым экземпляром перечня:
 * порядок задаёт контракт, и «первый» берётся из порождённого (долг `TD-69`).
 */
const DEFAULT_COMPANY_TYPE: CompanyType = COMPANY_TYPES[0];

export interface NoMembershipScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
}

export interface NoMembershipScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  submit(input: CompanyCreateRequest): Promise<void>;
  /** Подписи на новом языке; введённое остаётся на месте (долг `TD-72`). */
  relabel(next: Strings): void;
}

/**
 * Поле формы вместе с подписью и местом под сообщение сервера.
 *
 * Подпись, поле и место сообщения лежат в одном `label` намеренно: сообщение
 * ищется от поля вверх, и разложенные по разным узлам они разошлись бы.
 */
function field(fieldName: string): { label: HTMLLabelElement; caption: HTMLElement; input: HTMLInputElement } {
  const label = document.createElement('label');
  const caption = document.createElement('span');
  const input = document.createElement('input');

  label.className = 'form-field';
  caption.className = 'form-field__caption';
  input.name = fieldName;
  input.type = 'text';

  label.append(caption, input, createFieldMessageSlot());

  return { label, caption, input };
}

export function createNoMembershipScreen(
  options: NoMembershipScreenOptions,
): NoMembershipScreen {
  const { client } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const explanation = document.createElement('p');
  const form = document.createElement('form');
  const submitButton = document.createElement('button');

  element.className = 'screen screen-no-membership';
  submitButton.type = 'submit';
  submitButton.className = 'action action--primary';

  const nameField = field('name');
  const taxIdField = field('tax_id');
  const addressField = field('legal_address');

  const typeLabel = document.createElement('label');
  const typeCaption = document.createElement('span');
  const typeInput = document.createElement('select');

  typeLabel.className = 'form-field';
  typeCaption.className = 'form-field__caption';
  typeInput.name = 'type';

  for (const value of COMPANY_TYPES) {
    const choice = document.createElement('option');

    // **Значение уходит серверу, подпись читает человек, и это разные вещи**
    // (долг `TD-69`, решение `D188`). Прежде здесь стояло одно и то же, и
    // довод «сервер понимает величину буквально» верен ровно для `value`:
    // человек выбирал `transport` английским словом, а на соседнем экране тот
    // же тип был подписан «Перевозчик». Подпись берётся из единственного её
    // места — того же, которым подписан профиль фирмы.
    choice.value = value;
    typeInput.append(choice);
  }

  /**
   * Подписи экрана из текущего набора строк — единственное место, где они
   * ставятся: и при сборке, и при смене языка. Поля и введённое в них этим не
   * трогаются (долг `TD-72`).
   */
  function label(): void {
    heading.textContent = strings.no_membership_title;
    explanation.textContent = strings.no_membership_explain;
    submitButton.textContent = strings.company_create_submit;
    nameField.caption.textContent = strings.company_name_label;
    taxIdField.caption.textContent = strings.company_tax_id_label;
    addressField.caption.textContent = strings.company_legal_address_label;
    typeCaption.textContent = strings.company_form_type_label;

    COMPANY_TYPES.forEach((value, index) => {
      const choice = typeInput.options[index];

      if (choice !== undefined) {
        // Подпись типа — строителем по префиксу из порождённого перечня, без
        // рукописной таблицы (долг `TD-73`).
        choice.textContent = enumLabel(strings, companyTypeStringKey(value));
      }
    });
  }

  label();

  typeLabel.append(typeCaption, typeInput, createFieldMessageSlot());

  form.className = 'form';
  form.append(nameField.label, typeLabel, taxIdField.label, addressField.label, submitButton);

  let state: ScreenState | null = screenStateFor({ kind: 'done', empty: true });
  let chosenType: CompanyType = DEFAULT_COMPANY_TYPE;

  function render(): void {
    const parts: HTMLElement[] = [heading, explanation, form];

    if (state !== null) {
      parts.push(renderScreenState(state, strings));
    }

    element.replaceChildren(...parts);
  }

  function entered(): CompanyCreateRequest {
    return {
      name: nameField.input.value,
      type: chosenType,
      tax_id: taxIdField.input.value,
      legal_address: addressField.input.value,
    };
  }

  async function submit(input: CompanyCreateRequest): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    renderFieldMessages(form, []);
    render();

    try {
      const company = await client.createCompany(input);

      state = screenStateFor({ kind: 'done', empty: false });
      renderFieldMessages(form, []);
      render();

      // Успех уводит на адрес созданной фирмы: она уже существует, и оставлять
      // человека на экране «фирм у вас нет» значило бы показывать неправду.
      navigate({ kind: 'company', companyId: company.id });

      return;
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;

      state = screenStateFor({ kind: 'failed', code: failure?.code ?? null });
      render();
      renderFieldMessages(form, failure?.details ?? []);
    }
  }

  typeInput.addEventListener('change', () => {
    const value = COMPANY_TYPES[typeInput.selectedIndex];

    if (value !== undefined) {
      chosenType = value;
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    void submit(entered());
  });

  function relabel(next: Strings): void {
    strings = next;
    label();
    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    submit,
    relabel,
  };
}
